/**
 * HomePreview.jsx - Pilot Preview of New Home Layout
 * 
 * Same layout as Qualifying page but with different action buttons:
 * Feed, Members, Post, Book (instead of Play, Games, Settle, Chat)
 * 
 * This is a PREVIEW component - not yet integrated into main navigation
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { PlusCircle, Play, Trophy, DollarSign, MessageCircle, Rss, Users, Edit3, Calendar } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

// Main HomePreview Component
const HomePreview = ({ user }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch dashboard stats
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/stats`);
        setStats(response.data);
      } catch (error) {
        console.error("Error fetching stats:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  // Determine if user is qualified (has calculated handicap)
  const isQualified = stats?.handicap !== null && stats?.handicap !== undefined;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="dark-spinner" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto animate-fade-in" data-testid="home-preview-page">
      
      {/* Tab Bar: Feed | Members | Post | Book | Qualified */}
      <div className="flex items-center gap-4 px-4 py-3 border-b border-[var(--ps-border)] overflow-x-auto">
        <button
          className="text-[var(--ps-text-primary)] font-medium whitespace-nowrap pb-1"
          style={{ borderBottom: '2px solid var(--ps-text-primary)' }}
          data-testid="feed-tab"
        >
          Feed
        </button>
        <button
          onClick={() => navigate('/buddies')}
          className="text-[var(--ps-text-muted)] font-medium whitespace-nowrap hover:text-[var(--ps-text-secondary)] transition-colors"
          data-testid="members-tab"
        >
          Members
        </button>
        <button
          onClick={() => navigate('/home?tab=post')}
          className="font-bold whitespace-nowrap transition-colors"
          style={{
            color: '#3b82f6',
            textShadow: '0 0 8px rgba(59, 130, 246, 0.8), 0 0 16px rgba(59, 130, 246, 0.5)'
          }}
          data-testid="post-tab"
        >
          Post
        </button>
        <button
          onClick={() => navigate('/book')}
          className="font-bold whitespace-nowrap transition-colors"
          style={{
            color: '#22c55e',
            textShadow: '0 0 8px rgba(34, 197, 94, 0.8), 0 0 16px rgba(34, 197, 94, 0.5)'
          }}
          data-testid="book-tab"
        >
          Book
        </button>
        <button
          onClick={() => navigate('/dashboard')}
          className="font-bold whitespace-nowrap transition-colors"
          style={{
            color: isQualified ? '#22c55e' : '#f97316',
            textShadow: isQualified
              ? '0 0 8px rgba(34, 197, 94, 0.8), 0 0 16px rgba(34, 197, 94, 0.5)'
              : '0 0 8px rgba(249, 115, 22, 0.8), 0 0 16px rgba(249, 115, 22, 0.5)'
          }}
          data-testid="qualifier-tab"
        >
          {isQualified ? 'Qualified' : 'Qualifying'}
        </button>
      </div>
      
      {/* Main Content */}
      <div className="p-4">
      
        {/* Compact Handicap Card - Matches screenshot style */}
        <div 
          className="dark-card p-4 mb-4 cursor-pointer hover:border-[var(--ps-primary)] transition-all"
          onClick={() => navigate('/add-score')}
          data-testid="handicap-card"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* Circular Handicap Number */}
              <div className="w-16 h-16 rounded-lg bg-[var(--ps-primary)] flex items-center justify-center">
                <span className="text-2xl font-black text-white">
                  {stats?.handicap !== null && stats?.handicap !== undefined 
                    ? stats.handicap.toFixed(1) 
                    : '--'}
                </span>
              </div>
              <div>
                <p className="text-[var(--ps-text-muted)] text-sm">Handicap</p>
                <p className="text-[var(--ps-text-primary)] font-bold text-lg">Index</p>
              </div>
            </div>
            <PlusCircle className="w-6 h-6 text-[var(--ps-text-muted)]" />
          </div>
          
          {/* Stats Row */}
          <div className="grid grid-cols-4 gap-2 mt-4 pt-4 border-t border-[var(--ps-border)]">
            <div className="text-center">
              <p className="text-[var(--ps-text-muted)] text-xs uppercase">RNDS</p>
              <p className="text-[var(--ps-text-primary)] font-bold text-lg">
                {(() => {
                  const effectiveRounds = (stats?.eighteen_hole_count || 0) + ((stats?.unpaired_nine_count || 0) * 0.5);
                  return effectiveRounds % 1 === 0 ? effectiveRounds : effectiveRounds.toFixed(1);
                })()}
              </p>
            </div>
            <div className="text-center">
              <p className="text-[var(--ps-text-muted)] text-xs uppercase">AVG</p>
              <p className="text-[var(--ps-text-primary)] font-bold text-lg">{stats?.average_score?.toFixed(0) || '--'}</p>
            </div>
            <div className="text-center">
              <p className="text-[var(--ps-success)] text-xs uppercase">BEST</p>
              <p className="text-[var(--ps-success)] font-bold text-lg">{stats?.best_score || '--'}</p>
            </div>
            <div className="text-center">
              <p className="text-[var(--ps-error)] text-xs uppercase">WORST</p>
              <p className="text-[var(--ps-error)] font-bold text-lg">{stats?.worst_score || '--'}</p>
            </div>
          </div>
        </div>

        {/* Feed Content Placeholder - Would show actual posts here */}
        <div className="space-y-4">
          <div className="dark-card p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-green-600 flex items-center justify-center">
                <span className="text-white font-bold">C</span>
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-[var(--ps-text-primary)]">Chad DeSano</span>
                  <span className="px-2 py-0.5 rounded-full bg-[var(--ps-primary)]/20 text-[var(--ps-primary)] text-xs font-medium">
                    6 HCP
                  </span>
                </div>
                <p className="text-xs text-[var(--ps-text-muted)]">4d ago • General</p>
              </div>
            </div>
            <p className="text-[var(--ps-text-primary)] mb-3">Washakie GC North Branch Mi</p>
            <div className="rounded-lg overflow-hidden bg-[var(--ps-bg-elevated)] h-48 flex items-center justify-center">
              <span className="text-[var(--ps-text-muted)]">[Golf Course Image]</span>
            </div>
          </div>
        </div>
      
      </div>{/* End Main Content */}
    </div>
  );
};

export default HomePreview;
